/**
 * frontend/src/api.ts
 * ====================
 * Cliente HTTP para la API REST del Agente Conciliador.
 *
 * Principio de independencia:
 *   Este es el ÚNICO módulo que conoce las URLs del backend.
 *   Si la API cambia de base URL o de protocolo, solo este archivo cambia.
 *   Las vistas no hacen fetch() directamente.
 *
 * Toda request pasa por `pedir()`, que aplica un timeout y traduce los errores
 * a ApiError. Sin timeout, una request que nunca responde deja la UI colgada
 * en "Iniciando…" para siempre.
 */

import type {
  ColumnProfile,
  ColumnProfileResponse,
  PeriodoDetalle,
  Preferences,
  RespuestaDeteccion,
  RespuestaEstado,
  RespuestaIniciar,
  RespuestaResultado,
  SeleccionPerfil,
} from "./types";

const BASE_URL          = "/api/conciliar";
const PERFILES_BASE_URL = "/api/perfiles";
const SESION_URL        = "/api/sesion";
const TERMINOS_URL      = "/api/terminos";
const REPORTE_URL       = "/api/soporte/reporte";
const OPINION_URL       = "/api/soporte/opinion";

/**
 * Timeouts por tipo de operación.
 *
 * Las subidas de archivo y el parseo son lentos; el polling debe fallar rápido
 * para no encadenar requests colgadas.
 */
const TIMEOUT_MS = {
  rapido: 10_000,   // lecturas simples: estado, listados
  normal: 30_000,   // escrituras y borrados
  subida: 120_000,  // requests que suben y parsean archivos
} as const;

// ── Error tipificado ─────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    /** true si la request expiró o la red falló: reintentar puede funcionar. */
    public readonly esTimeout = false
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ── Núcleo HTTP ──────────────────────────────────────────────────────────────

interface OpcionesPedido extends RequestInit {
  /** Perfil de timeout. Default: "normal". */
  timeout?: keyof typeof TIMEOUT_MS;
  /** "archivo" devuelve el contenido y su nombre en vez de parsear JSON. */
  respuesta?: "json" | "archivo";
  /** Texto para el mensaje de error, ej: "consultar el estado". */
  accion: string;
}

/**
 * Ejecuta una request con timeout y devuelve la respuesta JSON parseada.
 *
 * Aborta pasado el timeout para que ninguna vista quede esperando de forma
 * indefinida. Los errores del backend conservan su `detail` — están redactados
 * para el usuario final y no tiene sentido reemplazarlos por un texto genérico.
 *
 * @throws ApiError siempre que la request no termine con 2xx.
 */
async function pedir<T>(url: string, opciones: OpcionesPedido): Promise<T> {
  const { timeout = "normal", accion, respuesta = "json", ...init } = opciones;
  const limiteMs = TIMEOUT_MS[timeout];

  const controlador = new AbortController();
  const temporizador = setTimeout(() => controlador.abort(), limiteMs);

  let res: Response;
  try {
    // `include` y no el default: en desarrollo el frontend corre en otro
    // puerto, y sin esto la cookie de sesión no viaja y todo da 401.
    res = await fetch(url, { ...init, credentials: "include", signal: controlador.signal });
  } catch (err) {
    // AbortError = venció el timeout. Cualquier otro fallo acá es de red.
    const expiro = err instanceof DOMException && err.name === "AbortError";
    throw new ApiError(
      expiro
        ? `La operación tardó más de ${Math.round(limiteMs / 1000)} segundos al ${accion}. ` +
          "Verifique que el servidor esté respondiendo e intente de nuevo."
        : `No se pudo conectar con el servidor al ${accion}.`,
      0,
      true
    );
  } finally {
    clearTimeout(temporizador);
  }

  if (!res.ok) {
    // El texto de `statusText` no sirve como respaldo: lo escribe el servidor
    // HTTP, no la aplicación, y llega en inglés. Un "Method Not Allowed" en
    // pantalla no le dice nada al usuario y contradice la regla de que nunca
    // ve detalles técnicos.
    const cuerpo = await res.json().catch(() => ({ detail: null }));

    // La sesión se puede vencer en medio de cualquier pantalla. Avisar desde
    // acá —el único lugar que ve todas las respuestas— evita que cada vista
    // tenga que reconocer el 401 por su cuenta, y que una se olvide.
    //
    // El ingreso queda afuera: ahí un 401 es "esa contraseña no es", no "se
    // venció la sesión". Avisar también en ese caso vuelve a dibujar la
    // pantalla de ingreso y se lleva puesto el mensaje de error que el usuario
    // tiene que leer.
    if (res.status === 401 && url !== SESION_URL) _alPerderLaSesion?.();

    // Los errores escritos por la aplicación traen `detail` como texto y están
    // redactados para el usuario. Los de validación de FastAPI lo traen como
    // lista de objetos: interpolarlos daba "[object Object]" en pantalla. Que
    // llegue uno significa que el navegador mandó algo que el contrato no
    // acepta, así que no hay nada que el usuario pueda leer ahí.
    const detalle = typeof cuerpo.detail === "string" ? cuerpo.detail : null;
    throw new ApiError(
      detalle ?? `No se pudo completar la operación al ${accion}. Intente de nuevo; `
        + "si el problema persiste, comuníquese con soporte.",
      res.status
    );
  }

  if (respuesta === "archivo") {
    // El nombre lo decide el servidor: es el mismo que tendría en el correo.
    const nombre = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? "reporte";
    return { contenido: await res.blob(), nombre } as T;
  }

  // 204 No Content y respuestas vacías no traen JSON que parsear.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ── Sesión ───────────────────────────────────────────────────────────────────

let _alPerderLaSesion: (() => void) | null = null;

/** Registra qué hacer cuando el servidor responde que no hay sesión. */
export function alPerderLaSesion(accion: () => void): void {
  _alPerderLaSesion = accion;
}

export interface EstadoSesion {
  /** Vacío si no hay sesión iniciada. */
  usuario: string;
  /** false en instalaciones sin usuarios configurados. */
  acceso_requerido: boolean;
}

/**
 * Quién está usando la aplicación.
 *
 * Se consulta al arrancar para decidir entre el ingreso y la aplicación, sin
 * tener que provocar un 401 para averiguarlo.
 */
export async function consultarSesion(): Promise<EstadoSesion> {
  return pedir<EstadoSesion>(SESION_URL, {
    method: "GET",
    timeout: "rapido",
    accion: "verificar la sesión",
  });
}

export interface EstadoTerminos {
  version: string;
  aceptado: boolean;
  /** UTC; vacío si todavía no la aceptó. */
  aceptado_en: string;
}

/** Si quien ingresó aceptó esa versión de los términos. */
export async function consultarTerminos(version: string): Promise<EstadoTerminos> {
  return pedir<EstadoTerminos>(`${TERMINOS_URL}?version=${encodeURIComponent(version)}`, {
    method: "GET",
    timeout: "rapido",
    accion: "consultar los términos aceptados",
  });
}

/** Deja constancia en el servidor de que quien ingresó aceptó esa versión. */
export async function aceptarTerminos(version: string): Promise<EstadoTerminos> {
  return pedir<EstadoTerminos>(TERMINOS_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ version }),
    accion: "registrar la aceptación de los términos",
  });
}

export async function ingresar(usuario: string, contrasena: string): Promise<EstadoSesion> {
  return pedir<EstadoSesion>(SESION_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ usuario, contrasena }),
    accion: "ingresar",
  });
}

export async function salir(): Promise<void> {
  await pedir<void>(SESION_URL, {
    method: "DELETE",
    timeout: "rapido",
    accion: "cerrar la sesión",
  });
}

// ── Reporte a soporte ────────────────────────────────────────────────────────

export interface DatosReporte {
  asunto: string;
  comentario: string;
  /** En qué pantalla estaba. Lo agrega el componente, no el usuario. */
  pantalla: string;
  /** La conciliación en curso, si había una. */
  threadId: string | null;
  adjuntos: File[];
  /** El usuario aceptó que lo enviado salga de su equipo. */
  consentimiento: boolean;
}

export interface RespuestaReporte {
  /** Referencia corta para reclamar por este reporte. */
  referencia: string;
}

/**
 * Manda a soporte lo que el usuario está viendo.
 *
 * Va como multipart porque puede llevar un archivo. El timeout es el de las
 * subidas: el adjunto puede pesar varios megas en una conexión lenta.
 */
export async function enviarReporte(datos: DatosReporte): Promise<RespuestaReporte> {
  const cuerpo = new FormData();
  cuerpo.append("asunto", datos.asunto);
  cuerpo.append("comentario", datos.comentario);
  cuerpo.append("consentimiento", String(datos.consentimiento));
  cuerpo.append("pantalla", datos.pantalla);
  if (datos.threadId) cuerpo.append("thread_id", datos.threadId);
  for (const archivo of datos.adjuntos) cuerpo.append("adjuntos", archivo);

  return pedir<RespuestaReporte>(REPORTE_URL, {
    method: "POST",
    body: cuerpo,
    timeout: "subida",
    accion: "enviar el reporte",
  });
}

// ── POST /api/soporte/opinion ────────────────────────────────────────────────

export interface DatosOpinion {
  /** Por aspecto: la cara elegida (1 a 5, o null) y el comentario. */
  calificaciones: Record<string, { puntaje: number | null; comentario: string }>;
  comentario_general: string;
  pantalla: string;
}

export async function enviarOpinion(datos: DatosOpinion): Promise<{ enviada_por_correo: boolean }> {
  return pedir<{ enviada_por_correo: boolean }>(OPINION_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(datos),
    accion: "enviar su opinión",
  });
}

// ── POST /api/conciliar/iniciar ──────────────────────────────────────────────

export async function iniciarConciliacion(
  extracto: File,
  libroMayor: File,
  prefs?: Preferences,
  extractoPerfil?: SeleccionPerfil | null,
  libroPerfil?: SeleccionPerfil | null,
): Promise<RespuestaIniciar> {
  const form = new FormData();
  form.append("extracto", extracto);
  form.append("libro_mayor", libroMayor);

  // Preferencias del usuario → se pasan como campos adicionales del form
  if (prefs) {
    if (prefs.nombreEmpresa)
      form.append("nombre_empresa", prefs.nombreEmpresa);
    if (prefs.nombreArchivoTemplate)
      form.append("nombre_archivo_template", prefs.nombreArchivoTemplate);
    if (prefs.codigoCuentaLibroMayor)
      form.append("codigo_cuenta", prefs.codigoCuentaLibroMayor);
    if (prefs.emailDestinatarios)
      form.append("destinatarios_email", prefs.emailDestinatarios);
    // tolerancia_fechas_dias ya no se envía: vive en el perfil de mapeo.
    // El umbral, en cambio, es global a la corrida: 0 = desactivado y el
    // backend ya tiene ese default, así que no hace falta mandarlo.
    if (prefs.umbralDiferenciaIgnorable > 0)
      form.append(
        "umbral_diferencia_ignorable",
        String(prefs.umbralDiferenciaIgnorable),
      );
  }

  // Mapeo de columnas: nombre si el perfil está guardado, JSON completo si es
  // un borrador de uso único. Si no se manda nada, el backend auto-detecta.
  agregarPerfil(form, "extracto", extractoPerfil);
  agregarPerfil(form, "libro",    libroPerfil);

  return pedir<RespuestaIniciar>(`${BASE_URL}/iniciar`, {
    method: "POST",
    body: form,
    timeout: "subida",
    accion: "iniciar la conciliación",
  });
}

/**
 * Agrega al form los campos del perfil de mapeo de un archivo.
 *
 * Los nombres de campo siguen el contrato del backend:
 *   {prefijo}_perfil_id     → busca ese perfil guardado en el store
 *   {prefijo}_perfil_inline → usa el perfil enviado, sin persistirlo
 *
 * Se manda el id y no el nombre. El backend acepta ambos, pero el nombre no
 * identifica: si hay dos perfiles homónimos elige mal o rechaza el pedido.
 */
function agregarPerfil(
  form: FormData,
  prefijo: "extracto" | "libro",
  seleccion?: SeleccionPerfil | null,
): void {
  if (!seleccion) return;

  if (seleccion.tipo === "guardado") {
    form.append(`${prefijo}_perfil_id`, seleccion.id);
  } else {
    form.append(`${prefijo}_perfil_inline`, JSON.stringify(seleccion.perfil));
  }
}

// ── GET /api/conciliar/{thread_id}/estado ────────────────────────────────────

export async function consultarEstado(threadId: string): Promise<RespuestaEstado> {
  return pedir<RespuestaEstado>(`${BASE_URL}/${threadId}/estado`, {
    timeout: "rapido",
    accion: "consultar el estado",
  });
}

// ── POST /api/conciliar/{thread_id}/decision ─────────────────────────────────

// ── GET /api/conciliar/{thread_id}/detalle y /descargar ──────────────────────

/** Los movimientos de cada período, mientras la conciliación espera la decisión. */
export async function obtenerDetalle(threadId: string): Promise<{ periodos: PeriodoDetalle[] }> {
  return pedir<{ periodos: PeriodoDetalle[] }>(`${BASE_URL}/${threadId}/detalle`, {
    method: "GET",
    accion: "cargar los movimientos",
  });
}

/** El reporte en Excel o PDF, sin enviarlo: el mismo que llegaría por correo. */
export async function descargarReporte(
  threadId: string,
  formato: "excel" | "pdf",
): Promise<{ contenido: Blob; nombre: string }> {
  return pedir<{ contenido: Blob; nombre: string }>(`${BASE_URL}/${threadId}/descargar?formato=${formato}`, {
    method: "GET",
    timeout: "subida",
    respuesta: "archivo",
    accion: `preparar el ${formato === "excel" ? "Excel" : "PDF"}`,
  });
}

/**
 * @param destinatarios a quién se envía el reporte: los de Preferencias al
 *   momento de decidir. Solo cuentan al enviar.
 */
export async function enviarDecision(
  threadId: string,
  decision: "aprobar" | "cancelar",
  destinatarios = "",
): Promise<void> {
  await pedir<void>(`${BASE_URL}/${threadId}/decision`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ decision, destinatarios }),
    accion: "enviar la decisión",
  });
}

// ── GET /api/conciliar/{thread_id}/resultado ─────────────────────────────────

export async function obtenerResultado(threadId: string): Promise<RespuestaResultado> {
  return pedir<RespuestaResultado>(`${BASE_URL}/${threadId}/resultado`, {
    timeout: "rapido",
    accion: "obtener el resultado",
  });
}

// ── POST /api/perfiles/detectar ──────────────────────────────────────────────

export async function detectarColumnas(
  archivo: File,
  hoja?: string,
  filaEncabezado?: number,
): Promise<RespuestaDeteccion> {
  const form = new FormData();
  form.append("archivo", archivo);
  if (hoja) form.append("hoja", hoja);
  // 0 es un valor válido, así que se compara contra undefined y no por truthiness.
  if (filaEncabezado !== undefined) form.append("fila_encabezado", String(filaEncabezado));

  return pedir<RespuestaDeteccion>(`${PERFILES_BASE_URL}/detectar`, {
    method: "POST",
    body: form,
    timeout: "subida",
    accion: "detectar las columnas",
  });
}

// ── POST /api/perfiles/ ──────────────────────────────────────────────────────

export async function guardarPerfil(perfil: Partial<ColumnProfile>): Promise<ColumnProfileResponse> {
  return pedir<ColumnProfileResponse>(`${PERFILES_BASE_URL}/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(perfil),
    accion: "guardar el perfil",
  });
}

// ── GET /api/perfiles/ ───────────────────────────────────────────────────────

export async function listarPerfiles(tipoFuente?: string): Promise<ColumnProfileResponse[]> {
  const url = tipoFuente
    ? `${PERFILES_BASE_URL}/?tipo_fuente=${encodeURIComponent(tipoFuente)}`
    : `${PERFILES_BASE_URL}/`;

  return pedir<ColumnProfileResponse[]>(url, {
    timeout: "rapido",
    accion: "listar los perfiles",
  });
}

// ── POST /api/perfiles/validar ───────────────────────────────────────────────

export interface ValidarPerfilResponse {
  ok: boolean;
  mensaje: string;
  /** La primera fila tal como la entendió el parser. */
  fila_muestra: Record<string, string | number> | null;
  /** Formatos que no coinciden con el contenido. No bloquean. */
  advertencias?: string[];

  /**
   * Movimientos que el perfil reconoció de verdad — no filas del archivo.
   *
   * En un libro mayor filtrado por cuenta este número es mucho menor que las
   * filas crudas que reporta la detección. Es el que va a procesar la
   * conciliación, así que es el único que el wizard puede mostrar al guardar.
   *
   * Solo viene cuando `ok` es true.
   */
  total?: number;
  /** Primera fecha de los movimientos reconocidos (ISO). */
  fecha_min?: string | null;
  /** Última fecha de los movimientos reconocidos (ISO). */
  fecha_max?: string | null;
  /**
   * Desde cuándo hasta cuándo va el archivo, con el mes en letras.
   *
   * No es el `periodo_label` del resultado de la conciliación, que son los
   * meses procesados. Acá es el rango del archivo que se está mapeando.
   */
  rango_fechas_label?: string;
}

export async function validarPerfil(
  archivo: File,
  perfil: Partial<ColumnProfile>
): Promise<ValidarPerfilResponse> {
  const form = new FormData();
  form.append("archivo", archivo);
  form.append("perfil", JSON.stringify(perfil));

  return pedir<ValidarPerfilResponse>(`${PERFILES_BASE_URL}/validar`, {
    method: "POST",
    body: form,
    timeout: "subida",
    accion: "validar el perfil",
  });
}

// ── DELETE /api/perfiles/{id} ────────────────────────────────────────────────

export async function eliminarPerfil(id: string): Promise<void> {
  await pedir<void>(`${PERFILES_BASE_URL}/${encodeURIComponent(id)}`, {
    method: "DELETE",
    accion: "eliminar el perfil",
  });
}

// ── GET /api/perfiles/exportar ───────────────────────────────────────────────

export interface SobreExportacion {
  version: number;
  exportado_en: string;
  perfiles: Partial<ColumnProfile>[];
}

export async function exportarPerfiles(tipoFuente?: string): Promise<SobreExportacion> {
  const url = tipoFuente
    ? `${PERFILES_BASE_URL}/exportar?tipo_fuente=${encodeURIComponent(tipoFuente)}`
    : `${PERFILES_BASE_URL}/exportar`;

  return pedir<SobreExportacion>(url, {
    timeout: "rapido",
    accion: "exportar los perfiles",
  });
}

// ── POST /api/perfiles/importar ──────────────────────────────────────────────

export interface ResultadoImportacion {
  importados: string[];
  omitidos: string[];
  fallidos: { nombre: string; motivo: string }[];
}

export async function importarPerfiles(
  sobre: SobreExportacion,
  sobrescribir = false,
): Promise<ResultadoImportacion> {
  return pedir<ResultadoImportacion>(`${PERFILES_BASE_URL}/importar`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...sobre, sobrescribir }),
    accion: "importar los perfiles",
  });
}
